import { useState } from 'react';
import { ListChecks, Plus, Check, Trash2, StickyNote } from 'lucide-react';
import { PageShell } from '@/components/layout/PageShell';
import { EmptyState } from '@/components/ui/EmptyState';
import { useTodosShared } from '@/context/TodosContext';
import { useTodoWidget } from '@/store/todoWidget';
import { TODO_COLORS, TODO_COLOR_ORDER, type TodoColorKey } from '@/lib/todoColors';

export function Tarefas() {
  const todos  = useTodosShared();
  const widget = useTodoWidget();
  const [addingTaskFor, setAddingTaskFor]     = useState<string | null>(null);
  const [newTaskContent, setNewTaskContent]   = useState('');
  const [newCatName, setNewCatName]           = useState('');
  const [addingCategory, setAddingCategory]   = useState(false);

  async function handleAddTask(categoryId: string) {
    if (!newTaskContent.trim()) return;
    await todos.addTask(categoryId, newTaskContent.trim()).catch(console.error);
    setNewTaskContent('');
    setAddingTaskFor(null);
  }

  async function handleAddCategory() {
    if (!newCatName.trim()) return;
    const usedColors = new Set(todos.categories.map((c) => c.color));
    const idx = todos.categories.length % TODO_COLOR_ORDER.length;
    const color: TodoColorKey =
      TODO_COLOR_ORDER.find((k) => !usedColors.has(k))
      ?? TODO_COLOR_ORDER[idx]
      ?? 'yellow';
    await todos.addCategory(newCatName.trim(), color).catch(console.error);
    setNewCatName('');
    setAddingCategory(false);
  }

  async function handleDeleteCategory(id: string, hasTasks: boolean) {
    if (hasTasks && !window.confirm('Esta categoria tem tarefas. Deseja excluí-la mesmo assim?')) return;
    await todos.deleteCategory(id).catch(console.error);
  }

  return (
    <PageShell
      title="Tarefas"
      subtitle="Seus to-dos pessoais organizados por categoria"
      maxWidth="full"
    >
      {/* Botão para restaurar o widget flutuante quando ele estiver oculto */}
      {(widget.dismissed || (!todos.loading && todos.categories.length === 0)) && (
        <div className="mb-4">
          <button
            onClick={() => widget.show()}
            className="btn-secondary flex items-center gap-2 text-sm"
          >
            <StickyNote size={14} />
            Mostrar widget flutuante
          </button>
        </div>
      )}

      {todos.loading ? (
        <div className="flex items-center justify-center py-16 text-ink-muted text-sm">
          Carregando…
        </div>
      ) : todos.error ? (
        <div className="rounded-lg bg-red-50 border border-red-200 p-4 text-sm text-red-700">
          {todos.error}
        </div>
      ) : todos.categories.length === 0 ? (
        <div>
          <EmptyState
            icon={ListChecks}
            title="Nenhuma categoria ainda"
            description="Crie uma categoria para começar a organizar suas tarefas."
          />
          {/* Criador de categoria SEMPRE disponível — sem ele seria impossível
              voltar a criar tarefas após excluir tudo */}
          <div className="max-w-xs mx-auto mt-2">
            {addingCategory ? (
              <div className="card card-pad flex items-center gap-2">
                <input
                  autoFocus
                  value={newCatName}
                  onChange={(e) => setNewCatName(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter')  void handleAddCategory();
                    if (e.key === 'Escape') { setAddingCategory(false); setNewCatName(''); }
                  }}
                  placeholder="Nome da categoria…"
                  className="flex-1 text-sm bg-transparent outline-none text-ink placeholder:text-ink-muted"
                />
                <button onClick={() => void handleAddCategory()} className="btn-primary !py-1 !px-2 text-xs">
                  Criar
                </button>
              </div>
            ) : (
              <button
                onClick={() => setAddingCategory(true)}
                className="flex items-center justify-center gap-2 text-sm text-ink-muted hover:text-ink transition-colors border border-dashed border-border rounded-xl px-4 py-3 w-full"
              >
                <Plus size={14} /> Nova categoria
              </button>
            )}
          </div>
        </div>
      ) : (
        <div className="flex gap-4 overflow-x-auto pb-4 items-start">
          {todos.categories.map((cat) => {
            const colors = TODO_COLORS[cat.color] ?? TODO_COLORS.yellow;
            const pending = cat.tasks.filter((t) => !t.done).length;
            return (
              <div
                key={cat.id}
                className="card flex-shrink-0 w-72 flex flex-col"
                style={{ borderTop: `3px solid ${colors.bg}` }}
              >
                {/* Cabeçalho da categoria */}
                <div className="card-pad border-b border-border flex items-center justify-between gap-2 py-3">
                  <div className="flex items-center gap-2 min-w-0">
                    <div
                      className="w-3 h-3 rounded-full flex-shrink-0"
                      style={{ backgroundColor: colors.bg }}
                    />
                    <span className="font-semibold text-sm text-ink truncate">{cat.name}</span>
                    {pending > 0 && (
                      <span
                        className="text-[10px] font-bold px-1.5 py-0.5 rounded-full flex-shrink-0"
                        style={{ backgroundColor: colors.bg, color: colors.ink }}
                      >
                        {pending}
                      </span>
                    )}
                  </div>
                  <button
                    onClick={() => handleDeleteCategory(cat.id, cat.tasks.length > 0)}
                    className="text-ink-muted hover:text-negative transition-colors flex-shrink-0"
                    title="Excluir categoria"
                  >
                    <Trash2 size={14} />
                  </button>
                </div>

                {/* Lista de tarefas */}
                <div className="card-pad flex-1 py-2 space-y-1">
                  {cat.tasks.length === 0 && (
                    <p className="text-xs text-ink-muted italic py-2">Nenhuma tarefa.</p>
                  )}
                  {cat.tasks.map((task) => (
                    <div key={task.id} className="flex items-start gap-2 group py-1">
                      <button
                        onClick={() => void todos.toggleTask(task.id).catch(console.error)}
                        className="flex-shrink-0 mt-0.5"
                        style={{
                          width: 15, height: 15, borderRadius: 3,
                          border: `1.5px solid ${colors.bg}`,
                          background: task.done ? colors.bg : 'transparent',
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          cursor: 'pointer',
                        }}
                      >
                        {task.done && <Check size={9} color={colors.ink} strokeWidth={3} />}
                      </button>
                      <span
                        className="flex-1 text-sm text-ink leading-snug"
                        style={{
                          textDecoration: task.done ? 'line-through' : 'none',
                          opacity: task.done ? 0.5 : 1,
                        }}
                      >
                        {task.content}
                      </span>
                      <button
                        onClick={() => void todos.deleteTask(task.id).catch(console.error)}
                        className="opacity-0 group-hover:opacity-100 text-ink-muted hover:text-negative transition-all flex-shrink-0"
                      >
                        <Trash2 size={12} />
                      </button>
                    </div>
                  ))}

                  {/* Adicionar tarefa */}
                  {addingTaskFor === cat.id ? (
                    <div className="flex items-center gap-2 pt-1">
                      <input
                        autoFocus
                        value={newTaskContent}
                        onChange={(e) => setNewTaskContent(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter')  void handleAddTask(cat.id);
                          if (e.key === 'Escape') { setAddingTaskFor(null); setNewTaskContent(''); }
                        }}
                        placeholder="Nova tarefa…"
                        className="flex-1 text-sm border-b border-border bg-transparent outline-none py-0.5 text-ink placeholder:text-ink-muted"
                      />
                      <button
                        onClick={() => void handleAddTask(cat.id)}
                        className="btn-primary !py-1 !px-2 text-xs"
                      >
                        Adicionar
                      </button>
                    </div>
                  ) : (
                    <button
                      onClick={() => setAddingTaskFor(cat.id)}
                      className="flex items-center gap-1.5 text-xs text-ink-muted hover:text-ink transition-colors pt-1"
                    >
                      <Plus size={12} /> Adicionar tarefa
                    </button>
                  )}
                </div>
              </div>
            );
          })}

          {/* Nova categoria */}
          <div className="flex-shrink-0 w-72">
            {addingCategory ? (
              <div className="card card-pad flex items-center gap-2">
                <input
                  autoFocus
                  value={newCatName}
                  onChange={(e) => setNewCatName(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter')  void handleAddCategory();
                    if (e.key === 'Escape') { setAddingCategory(false); setNewCatName(''); }
                  }}
                  placeholder="Nome da categoria…"
                  className="flex-1 text-sm bg-transparent outline-none text-ink placeholder:text-ink-muted"
                />
                <button onClick={() => void handleAddCategory()} className="btn-primary !py-1 !px-2 text-xs">
                  Criar
                </button>
              </div>
            ) : (
              <button
                onClick={() => setAddingCategory(true)}
                className="flex items-center gap-2 text-sm text-ink-muted hover:text-ink transition-colors border border-dashed border-border rounded-xl px-4 py-3 w-full"
              >
                <Plus size={14} /> Nova categoria
              </button>
            )}
          </div>
        </div>
      )}
    </PageShell>
  );
}
